import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, fmtMoney, fmtDate, fileUrl } from "@/lib/api";
import { useAssetClasses } from "@/lib/assetClasses";
import { useAuth } from "@/lib/auth";
import BulkMoveBar from "@/components/BulkMoveBar";
import { MagnifyingGlass, Plus, Rows, SquaresFour, Image as ImageIcon, CaretUp, CaretDown, ArrowsDownUp } from "@phosphor-icons/react";
import ClassSuggestionBanner from "@/components/ClassSuggestionBanner";
import HelpLink from "@/components/HelpLink";

const SORT_KEY = "fl.ledgerSort";
const WARRANTY_FILTER_KEY = "fl.ledgerWarrantyFilter";

const WARRANTY_FILTERS = ["all", "unregistered", "expiring", "registered"];

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  return Math.ceil((d - Date.now()) / 86400000);
}

function matchesWarrantyFilter(asset, filter) {
  if (filter === "all") return true;
  const status = asset.warranty_status;
  if (filter === "unregistered") return status === "unregistered";
  if (filter === "registered") return status === "registered";
  if (filter === "expiring") {
    if (status !== "registered") return false;
    const days = daysUntil(asset.warranty_expiration_date);
    return days != null && days >= 0 && days <= 30;
  }
  return true;
}

function loadWarrantyFilter() {
  try {
    const raw = localStorage.getItem(WARRANTY_FILTER_KEY);
    if (raw && WARRANTY_FILTERS.includes(raw)) return raw;
  } catch {
    // ignore
  }
  return "all";
}

function saveWarrantyFilter(value) {
  try {
    if (value && value !== "all") {
      localStorage.setItem(WARRANTY_FILTER_KEY, value);
    } else {
      localStorage.removeItem(WARRANTY_FILTER_KEY);
    }
  } catch {
    // ignore
  }
}

const COMPARATORS = {
  name: (a, b) => strCompare(a.name, b.name),
  category: (a, b) => strCompare(a.category, b.category),
  brand_model: (a, b) =>
    strCompare(
      `${a.brand || ""} ${a.model || ""}`.trim(),
      `${b.brand || ""} ${b.model || ""}`.trim(),
    ),
  serial: (a, b) => strCompare(a.serial, b.serial),
  location: (a, b) => strCompare(a.location, b.location),
  fmv: (a, b) => numCompare(a.fmv, b.fmv),
  rcv: (a, b) => numCompare(a.rcv, b.rcv),
};

const BLANK_GETTERS = {
  name: (a) => !(a.name || "").toString().trim(),
  category: (a) => !(a.category || "").toString().trim(),
  brand_model: (a) => !`${a.brand || ""} ${a.model || ""}`.trim(),
  serial: (a) => !(a.serial || "").toString().trim(),
  location: (a) => !(a.location || "").toString().trim(),
  fmv: (a) => a.fmv == null,
  rcv: (a) => a.rcv == null,
};

function strCompare(a, b) {
  // Both values are guaranteed non-blank by the wrapper.
  const av = a.toString().trim();
  const bv = b.toString().trim();
  return av.localeCompare(bv, undefined, { sensitivity: "base", numeric: true });
}

function numCompare(a, b) {
  // Both values are guaranteed non-null by the wrapper.
  return Number(a) - Number(b);
}

function loadSort() {
  try {
    const raw = localStorage.getItem(SORT_KEY);
    if (!raw) return { key: null, dir: null };
    const parsed = JSON.parse(raw);
    if (parsed && COMPARATORS[parsed.key] && (parsed.dir === "asc" || parsed.dir === "desc")) {
      return parsed;
    }
  } catch {
    // ignore parse / storage failures
  }
  return { key: null, dir: null };
}

function saveSort(sort) {
  try {
    if (sort.key && sort.dir) {
      localStorage.setItem(SORT_KEY, JSON.stringify(sort));
    } else {
      localStorage.removeItem(SORT_KEY);
    }
  } catch {
    // ignore
  }
}

export default function Assets() {
  const { canWrite, activeLedgerId, households } = useAuth();
  const activeHouseholdId = useMemo(
    () => (households || []).find((h) => h.id === activeLedgerId)?.id || null,
    [households, activeLedgerId],
  );
  // v3.15.3 — bulk-move selection. IDs live only in local state; clearing
  // on unmount or ledger switch is handled by the effect below.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState("table");
  const [sort, setSort] = useState(loadSort);
  const [warrantyFilter, setWarrantyFilter] = useState(loadWarrantyFilter);
  // v3.15.4 — room hero photos keyed by lowercase name. Only relevant
  // on household ledgers; personal ledgers never populate this.
  const [roomPhotos, setRoomPhotos] = useState({});
  useEffect(() => {
    if (!activeHouseholdId) { setRoomPhotos({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/households/${activeHouseholdId}/rooms`);
        if (!cancelled) setRoomPhotos(r.data?.room_photos || {});
      } catch {
        if (!cancelled) setRoomPhotos({});
      }
    })();
    return () => { cancelled = true; };
  }, [activeHouseholdId]);
  // v3.15 — room filter. Client-side (not passed to the API) because
  // rooms are just distinct values of `location` and the ledger's
  // room list is small. Persist to localStorage so a user who's
  // "walking the garage" this evening picks up where they left off.
  const [room, setRoom] = useState(() => {
    try { return localStorage.getItem("fl.assets.room") || "all"; }
    catch { return "all"; }
  });
  const setRoomPersisted = (v) => {
    setRoom(v);
    try {
      if (v === "all") localStorage.removeItem("fl.assets.room");
      else localStorage.setItem("fl.assets.room", v);
    } catch { /* ignore */ }
  };
  const { classes: assetClasses, labelFor, colorFor } = useAssetClasses();
  const categoryLabel = labelFor;

  const load = () => {
    setLoading(true);
    const params = {};
    if (cat !== "all") params.category = cat;
    if (q) params.q = q;
    api
      .get("/assets", { params })
      .then((r) => setAssets(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);

  // Clear selection when the ledger switches — carrying selected IDs
  // across households would silently no-op on the backend (rows aren't
  // in the new ledger) and confuse the user.
  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLedgerId]);

  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const totalFmv = useMemo(() => assets.reduce((s, a) => s + (a.fmv || 0), 0), [assets]);

  // Counts for the warranty filter chips (computed from the current category-
  // filtered list so the numbers stay relevant when a class chip is active).
  const warrantyCounts = useMemo(() => {
    let unregistered = 0;
    let expiring = 0;
    let registered = 0;
    for (const a of assets) {
      if (a.warranty_status === "unregistered") unregistered += 1;
      else if (a.warranty_status === "registered") {
        registered += 1;
        const days = daysUntil(a.warranty_expiration_date);
        if (days != null && days >= 0 && days <= 30) expiring += 1;
      }
    }
    return { unregistered, expiring, registered, total: assets.length };
  }, [assets]);

  const filteredByWarranty = useMemo(
    () => assets.filter((a) => matchesWarrantyFilter(a, warrantyFilter)),
    [assets, warrantyFilter],
  );

  // Distinct room list from the assets currently in view (so the dropdown
  // reflects reality, including any legacy free-form location values
  // that aren't in the household rooms list).
  const rooms = useMemo(() => {
    const set = new Set();
    for (const a of assets) {
      const r = (a.location || "").trim();
      if (r) set.add(r);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  // v3.15.1 — reset a stale room selection when the loaded ledger
  // doesn't contain it. Without this a user who switches households
  // while a `fl.assets.room` from the previous ledger is persisted
  // sees "0 assets match" with the dropdown reading "All rooms"
  // because the persisted value doesn't match any <option>.
  useEffect(() => {
    if (loading) return;
    if (room === "all" || room === "__none__") return;
    if (!rooms.some((r) => r.toLowerCase() === room.toLowerCase())) {
      setRoomPersisted("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, loading]);

  const filteredByRoom = useMemo(() => {
    if (room === "all") return filteredByWarranty;
    if (room === "__none__") return filteredByWarranty.filter((a) => !(a.location || "").trim());
    const r = room.toLowerCase();
    return filteredByWarranty.filter((a) => (a.location || "").trim().toLowerCase() === r);
  }, [filteredByWarranty, room]);

  const sortedAssets = useMemo(() => {
    if (!sort.key || !COMPARATORS[sort.key]) return filteredByRoom;
    const cmp = COMPARATORS[sort.key];
    const isBlank = BLANK_GETTERS[sort.key];
    const sign = sort.dir === "desc" ? -1 : 1;
    return [...filteredByRoom].sort((a, b) => {
      // Blanks/nulls pin to the bottom in BOTH directions — handle before
      // applying the sign multiplier or desc would flip them to the top.
      const aBlank = isBlank(a);
      const bBlank = isBlank(b);
      if (aBlank && bBlank) return 0;
      if (aBlank) return 1;
      if (bBlank) return -1;
      return sign * cmp(a, b);
    });
  }, [filteredByRoom, sort]);

  const onWarrantyFilter = (value) => {
    setWarrantyFilter(value);
    saveWarrantyFilter(value);
  };

  const onSort = (key) => {
    setSort((prev) => {
      let next;
      if (prev.key !== key) next = { key, dir: "asc" };
      else if (prev.dir === "asc") next = { key, dir: "desc" };
      else next = { key: null, dir: null };
      saveSort(next);
      return next;
    });
  };

  return (
    <div data-testid="assets-page" className="space-y-8">
      <ClassSuggestionBanner onChange={load} />
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-[#C9C4B8] pb-6">
        <div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2 flex items-center gap-3">
            <span>The Ledger · Full Index</span>
            <HelpLink anchor="six-doors" label="Recording an asset" />
          </div>
          <h1 className="font-serif-display text-5xl text-[#0A192F] leading-none">Every line item.</h1>
          <p className="text-sm text-[#5C5F58] mt-3 max-w-xl">
            A permanent record of physical wealth — filterable by class, searchable by name, brand, model, serial, or location.
          </p>
        </div>
        {canWrite && (
          <Link
            to="/assets/new"
            data-testid="assets-add-btn"
            className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] self-start"
          >
            <Plus size={16} weight="bold" /> Record New
          </Link>
        )}
      </header>

      {/* Controls row */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex flex-wrap gap-1" data-testid="category-filters">
          <FilterChip active={cat === "all"} onClick={() => setCat("all")} testid="chip-all">
            All <span className="font-mono-ledger text-xs opacity-70 ml-1">{assets.length}</span>
          </FilterChip>
          {assetClasses.map((c) => (
            <FilterChip key={c.key} active={cat === c.key} onClick={() => setCat(c.key)} testid={`chip-${c.key}`}>
              {c.label}
            </FilterChip>
          ))}
        </div>

        <div className="flex gap-3 items-center">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F58]" />
            <input
              data-testid="assets-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the ledger…"
              className="bg-white border border-[#E0DDD6] pl-9 pr-3 py-2 text-sm w-64 focus:outline-none focus:border-[#1A3622]"
            />
          </div>
          {rooms.length > 0 && (
            <div className="flex items-center gap-2">
              {(() => {
                const cover = room && room !== "all" && room !== "__none__"
                  ? roomPhotos?.[room.toLowerCase()]
                  : null;
                if (!cover) return null;
                return (
                  <img
                    src={fileUrl(cover)}
                    alt=""
                    className="w-8 h-8 object-cover border border-[#E0DDD6] shrink-0"
                    data-testid="assets-room-filter-preview"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                );
              })()}
              <select
                value={room}
                onChange={(e) => setRoomPersisted(e.target.value)}
                data-testid="assets-room-filter"
                aria-label="Filter by room"
                className="bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
              >
                <option value="all">All rooms</option>
                {rooms.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
                <option value="__none__">— No room set</option>
              </select>
            </div>
          )}
          <div className="flex border border-[#E0DDD6] bg-white">
            <button
              data-testid="view-table"
              onClick={() => setView("table")}
              className={`p-2 ${view === "table" ? "bg-[#1A3622] text-[#F9F8F6]" : "text-[#5C5F58]"}`}
              aria-label="Table view"
            >
              <Rows size={16} />
            </button>
            <button
              data-testid="view-cards"
              onClick={() => setView("cards")}
              className={`p-2 ${view === "cards" ? "bg-[#1A3622] text-[#F9F8F6]" : "text-[#5C5F58]"}`}
              aria-label="Card view"
            >
              <SquaresFour size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Warranty filter row — surfaces only when the ledger has at least one
          unregistered or registered asset (otherwise the chips would always
          read zero). */}
      {(warrantyCounts.unregistered + warrantyCounts.registered) > 0 && (
        <div
          className="flex flex-wrap gap-1 items-center"
          data-testid="warranty-filters"
        >
          <span className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mr-2">
            Warranty
          </span>
          <FilterChip
            active={warrantyFilter === "all"}
            onClick={() => onWarrantyFilter("all")}
            testid="warranty-chip-all"
          >
            All
          </FilterChip>
          <FilterChip
            active={warrantyFilter === "unregistered"}
            onClick={() => onWarrantyFilter("unregistered")}
            testid="warranty-chip-unregistered"
          >
            Unregistered
            <span className="font-mono-ledger text-xs opacity-70 ml-1">
              {warrantyCounts.unregistered}
            </span>
          </FilterChip>
          <FilterChip
            active={warrantyFilter === "expiring"}
            onClick={() => onWarrantyFilter("expiring")}
            testid="warranty-chip-expiring"
          >
            Expiring &lt;30d
            <span className="font-mono-ledger text-xs opacity-70 ml-1">
              {warrantyCounts.expiring}
            </span>
          </FilterChip>
          <FilterChip
            active={warrantyFilter === "registered"}
            onClick={() => onWarrantyFilter("registered")}
            testid="warranty-chip-registered"
          >
            Registered
            <span className="font-mono-ledger text-xs opacity-70 ml-1">
              {warrantyCounts.registered}
            </span>
          </FilterChip>
        </div>
      )}

      {/* Summary strip */}
      <div className="flex gap-6 font-mono-ledger text-xs text-[#5C5F58] border-y border-[#E0DDD6] py-3" data-testid="summary-strip">
        <span>Items on page · <span className="text-[#0A192F] tabular-nums">{sortedAssets.length}</span></span>
        <span>FMV subtotal · <span className="text-[#0A192F] tabular-nums">{fmtMoney(totalFmv)}</span></span>
      </div>

      {/* Bulk-move bar — appears above the body whenever rows are ticked */}
      {canWrite && (
        <BulkMoveBar
          selectedIds={Array.from(selectedIds)}
          onClear={clearSelection}
          onMoved={() => { clearSelection(); load(); }}
          householdId={activeHouseholdId}
        />
      )}

      {/* Body */}
      {loading ? (
        <div className="text-sm text-[#5C5F58]" data-testid="assets-loading">Loading…</div>
      ) : sortedAssets.length === 0 ? (
        assets.length === 0
          ? <EmptyState canWrite={canWrite} />
          : room !== "all"
            ? <RoomFilterEmpty room={room} onClear={() => setRoomPersisted("all")} />
            : <WarrantyFilterEmpty filter={warrantyFilter} onClear={() => onWarrantyFilter("all")} />
      ) : view === "table" ? (
        <div className="border border-[#E0DDD6] bg-white overflow-x-auto" data-testid="assets-table">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-[#F9F8F6] border-b border-[#E0DDD6]">
              <tr>
                {canWrite && (
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      data-testid="bulk-select-all"
                      aria-label="Select all visible assets"
                      checked={sortedAssets.length > 0 && sortedAssets.every((a) => selectedIds.has(a.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(sortedAssets.map((a) => a.id)));
                        } else {
                          clearSelection();
                        }
                      }}
                      className="w-3.5 h-3.5 accent-[#1A3622] cursor-pointer"
                    />
                  </Th>
                )}
                <Th>#</Th>
                <Th aria-label="Thumbnail" className="w-14"></Th>
                <SortableTh sortKey="name" sort={sort} onSort={onSort}>Name</SortableTh>
                <SortableTh sortKey="category" sort={sort} onSort={onSort}>Class</SortableTh>
                <SortableTh sortKey="brand_model" sort={sort} onSort={onSort}>Brand / Model</SortableTh>
                <SortableTh sortKey="serial" sort={sort} onSort={onSort}>Serial</SortableTh>
                <SortableTh sortKey="location" sort={sort} onSort={onSort}>Room</SortableTh>
                <SortableTh sortKey="fmv" sort={sort} onSort={onSort} align="right">FMV</SortableTh>
                <SortableTh sortKey="rcv" sort={sort} onSort={onSort} align="right">RCV</SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedAssets.map((a, i) => (
                <tr
                  key={a.id}
                  data-testid={`asset-tr-${a.id}`}
                  className={`border-b border-[#E0DDD6] hover:bg-[#F9F8F6]/60 ${
                    selectedIds.has(a.id) ? "bg-[#F9F8F6]" : ""
                  }`}
                >
                  {canWrite && (
                    <td className="px-2 py-2 align-middle">
                      <input
                        type="checkbox"
                        data-testid={`bulk-select-${a.id}`}
                        aria-label={`Select ${a.name}`}
                        checked={selectedIds.has(a.id)}
                        onChange={() => toggleSelect(a.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-3.5 h-3.5 accent-[#1A3622] cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono-ledger text-xs text-[#5C5F58] tabular-nums">
                    {String(i + 1).padStart(3, "0")}
                  </td>
                  <td className="px-2 py-2">
                    <RowThumb asset={a} />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/assets/${a.id}`}
                      data-testid={`asset-row-${a.id}`}
                      className="text-[#0A192F] hover:underline underline-offset-4"
                    >
                      {a.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-[#5C5F58]">
                    <span className="inline-flex items-center gap-2" data-testid={`asset-class-chip-${a.id}`}>
                      <span
                        className="inline-block w-2 h-2"
                        style={{ backgroundColor: colorFor(a.category) }}
                        aria-hidden="true"
                      />
                      {categoryLabel(a.category)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#0A192F]">
                    {[a.brand, a.model].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td className="px-4 py-3 font-mono-ledger text-xs text-[#5C5F58]">{a.serial || "—"}</td>
                  <td className="px-4 py-3 text-[#5C5F58]" title={a.sub_location || undefined}>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const cover = a.location ? roomPhotos?.[a.location.trim().toLowerCase()] : null;
                        if (!cover) return null;
                        return (
                          <img
                            src={fileUrl(cover)}
                            alt=""
                            className="w-6 h-6 object-cover border border-[#E0DDD6] shrink-0"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        );
                      })()}
                      <div className="min-w-0">
                        <div>{a.location || a.sub_location || "—"}</div>
                        {a.location && a.sub_location && (
                          <span className="block text-[10px] font-mono-ledger text-[#5C5F58]/80 italic">
                            {a.sub_location}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-ledger text-[#0A192F] tabular-nums">
                    {fmtMoney(a.fmv)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-ledger text-[#5C5F58] tabular-nums">
                    {fmtMoney(a.rcv)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E0DDD6] bg-white" data-testid="assets-cards">
          {sortedAssets.map((a) => (
            <Link
              key={a.id}
              to={`/assets/${a.id}`}
              data-testid={`asset-card-${a.id}`}
              className="p-5 border-b border-r border-[#E0DDD6] hover:bg-[#F9F8F6]/60 transition-colors"
            >
              <CardThumb asset={a} />
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] mb-2">
                {categoryLabel(a.category)}
              </div>
              <div className="font-serif-display text-2xl text-[#0A192F] leading-tight mb-3">{a.name}</div>
              <div className="text-sm text-[#5C5F58] mb-1">
                {[a.brand, a.model].filter(Boolean).join(" · ") || "—"}
              </div>
              <div className="font-mono-ledger text-xs text-[#5C5F58] mb-4">{a.serial || "No serial on record"}</div>
              <div className="flex justify-between items-end pt-3 border-t border-[#E0DDD6]">
                <div>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">FMV</div>
                  <div className="font-mono-ledger text-sm text-[#0A192F] tabular-nums">{fmtMoney(a.fmv)}</div>
                </div>
                <div className="font-mono-ledger text-[10px] text-[#5C5F58]">{fmtDate(a.created_at)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-1.5 text-xs border transition-colors ${
        active
          ? "bg-[#0A192F] text-[#F9F8F6] border-[#0A192F]"
          : "bg-white text-[#5C5F58] border-[#E0DDD6] hover:border-[#0A192F] hover:text-[#0A192F]"
      }`}
    >
      {children}
    </button>
  );
}

function RowThumb({ asset }) {
  const path = asset.featured_photo_path || asset.photo_paths?.[0];
  if (!path) {
    return (
      <div
        className="w-10 h-10 bg-[#F9F8F6] border border-[#E0DDD6] flex items-center justify-center"
        data-testid={`row-thumb-empty-${asset.id}`}
      >
        <ImageIcon size={14} className="text-[#C9C4B8]" />
      </div>
    );
  }
  return (
    <img
      src={`${API}/files/${path}`}
      alt=""
      loading="lazy"
      className="w-10 h-10 object-cover border border-[#E0DDD6]"
      data-testid={`row-thumb-${asset.id}`}
    />
  );
}

function CardThumb({ asset }) {
  const path = asset.featured_photo_path || asset.photo_paths?.[0];
  if (!path) {
    return (
      <div
        className="aspect-[16/9] bg-[#F9F8F6] border border-[#E0DDD6] mb-4 flex items-center justify-center"
        data-testid={`card-thumb-empty-${asset.id}`}
      >
        <ImageIcon size={20} className="text-[#C9C4B8]" />
      </div>
    );
  }
  return (
    <div className="aspect-[16/9] bg-[#F9F8F6] border border-[#E0DDD6] mb-4 overflow-hidden">
      <img
        src={`${API}/files/${path}`}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover"
        data-testid={`card-thumb-${asset.id}`}
      />
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] ${className}`}>
      {children}
    </th>
  );
}

function SortableTh({ children, sortKey, sort, onSort, align = "left" }) {
  const active = sort.key === sortKey;
  const dir = active ? sort.dir : null;
  const Icon = dir === "asc" ? CaretUp : dir === "desc" ? CaretDown : ArrowsDownUp;
  const alignCls = align === "right" ? "text-right" : "text-left";
  const flexCls = align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={`px-4 py-3 font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] ${alignCls}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`sort-header-${sortKey}`}
        aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}
        title={
          active
            ? dir === "asc"
              ? "Sorted ascending · click for descending"
              : "Sorted descending · click to clear"
            : "Sort by this column"
        }
        className={`group inline-flex items-center gap-1 select-none transition-colors ${flexCls} ${
          active ? "text-[#0A192F]" : "text-[#5C5F58] hover:text-[#0A192F]"
        }`}
      >
        <span>{children}</span>
        <Icon
          size={10}
          weight={active ? "bold" : "regular"}
          data-testid={`sort-caret-${sortKey}`}
          className={active ? "opacity-100" : "opacity-30 group-hover:opacity-70"}
        />
      </button>
    </th>
  );
}

function EmptyState({ canWrite }) {
  return (
    <div className="border border-[#E0DDD6] bg-white px-8 py-16 text-center" data-testid="assets-empty">
      <p className="font-serif-display text-3xl text-[#0A192F] mb-3">No matches in the ledger.</p>
      <p className="text-sm text-[#5C5F58] max-w-md mx-auto mb-6">
        Nothing has been recorded under this filter yet. {canWrite ? "Begin by recording a new asset — the ledger only knows what it's told." : "This ledger is read-only for your role."}
      </p>
      {canWrite && (
        <Link
          to="/assets/new"
          data-testid="empty-add-btn"
          className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818]"
        >
          <Plus size={16} weight="bold" /> Record New
        </Link>
      )}
    </div>
  );
}


function RoomFilterEmpty({ room, onClear }) {
  const label = room === "__none__" ? "unassigned to any room" : `in "${room}"`;
  return (
    <div
      className="border border-dashed border-[#E0DDD6] bg-white px-6 py-12 text-center"
      data-testid="room-filter-empty"
    >
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        Room empty
      </div>
      <p className="text-sm text-[#5C5F58] mb-4">
        No assets {label} yet.
      </p>
      <button
        type="button"
        onClick={onClear}
        data-testid="room-filter-clear"
        className="text-sm text-[#1A3622] underline underline-offset-4 hover:text-[#0F2818]"
      >
        Clear room filter →
      </button>
    </div>
  );
}


function WarrantyFilterEmpty({ filter, onClear }) {
  const labels = {
    unregistered: "No unregistered warranties in this view.",
    expiring: "No warranties expiring within 30 days.",
    registered: "No registered warranties yet.",
  };
  return (
    <div
      className="border border-dashed border-[#E0DDD6] bg-white px-6 py-12 text-center"
      data-testid="warranty-filter-empty"
    >
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        Filter empty
      </div>
      <p className="text-sm text-[#5C5F58] mb-4">
        {labels[filter] || "No assets match the current filter."}
      </p>
      <button
        type="button"
        onClick={onClear}
        data-testid="warranty-filter-clear"
        className="text-sm text-[#1A3622] underline underline-offset-4 hover:text-[#0F2818]"
      >
        Clear warranty filter →
      </button>
    </div>
  );
}
